/**
 * ActiveMissionBar — lavender pill that hovers above the bottom tab
 * bar while today's mission is in "started" state. Founder feedback:
 * the Do beat of the mission ritual (Set → Do → Close) used to just
 * deep-link to the habits tab, then evaporate — the user lost the
 * sense of being "in a mission." This bar fixes that without
 * redesigning the underlying flow:
 *
 *   - Polls /api/missions/today; renders only when mission.status === "started".
 *   - Title pill is tappable and switches to My Day (where the Mission
 *     card lives, showing the same Started state). Lets the user
 *     navigate freely without losing their place.
 *   - "Complete" button opens the MissionRitual Close beat right
 *     here — same +25 XP + pillar pulse + auto-dismiss as the original.
 *
 * Stacks above the ActiveWorkoutSticky pill when both exist; uses
 * lavender so it visually echoes the Evening Review CTA (its sibling
 * mission surface) rather than the amber Mission card.
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { ShieldCheck, TrendingUp, Eye, ChevronRight, Check, Target } from "lucide-react";
import MissionRitual from "@/components/MissionRitual";

// Pillar icon map — mirrors the one in MissionCard / MissionRitual so
// the in-progress pill carries the same pillar identity as the rest
// of the mission flow. Generic `Target` icon was used before; this
// makes the bar visually consistent with the merged eyebrow language.
const PILLAR_ICON = {
  integrity: ShieldCheck,
  momentum: TrendingUp,
  clarity: Eye,
} as const;

type PillarKey = "integrity" | "momentum" | "clarity";
type SourceType = "habit" | "task" | "goal" | "vision";
type MissionStatus = "pending" | "started" | "completed" | "skipped";

interface Mission {
  id: string;
  date: string;
  source_type: SourceType;
  source_id: string;
  title: string;
  pillar: PillarKey;
  status: MissionStatus;
}

interface MissionResponse {
  date: string;
  mission: Mission | null;
}

// Routes where the sticky bar shouldn't render. Same list shape as
// ActiveWorkoutSticky — anything outside the authenticated /app
// experience (login, landing, password reset, marketing pages) is
// skipped. Keep these aligned.
const SKIP_PATHS = [
  "/reset-password",
  "/landing",
  "/login",
  "/white-paper",
  "/75-hard",
  "/challenges",
  "/privacy",
  "/terms",
  "/support",
  "/contact",
  "/faq",
  // /demo is the outreach landing page: a bar about today's mission has no
  // business there, and the visitor has no session yet anyway (T23).
  "/demo",
];

const SWITCH_TAB_EVENT = "trybeup:switch-tab";

export default function ActiveMissionBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [closeOpen, setCloseOpen] = useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const onSkippedRoute = SKIP_PATHS.some((p) =>
    location.pathname.startsWith(p)
  );

  // ── Why this query is gated (T23, 2026-08-23) ──────────────────────
  // This component is mounted app-wide in App.tsx, and hooks can't be
  // conditional — so the route check below used to happen AFTER the query
  // had already fired. On /demo that meant `GET /api/missions/today` went
  // out before DemoEntry had finished installing the GoTrue session,
  // logged a 403, and retried fine a moment later. Harmless, but it was
  // the only non-2xx request in a clean demo walk and the first thing any
  // technical evaluator with a console open would see.
  //
  // `enabled` is the fix rather than moving the query: it keeps the hook
  // unconditional (as the rules require) while stopping the request until
  // there is a session to make it with. Also skips the request entirely on
  // the public routes, which never needed it.
  const { data } = useQuery<MissionResponse>({
    queryKey: ["/api/missions/today"],
    enabled: !authLoading && isAuthenticated && !onSkippedRoute,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  if (onSkippedRoute) return null;

  const mission = data?.mission ?? null;
  if (!mission || mission.status !== "started") return null;

  // Defensive fallback: if backend ever returns a pillar value not in
  // our 3-key map, render a neutral Target icon instead of crashing
  // the floating in-progress pill with "Element type is invalid".
  const PillarIcon = PILLAR_ICON[mission.pillar] ?? Target;

  const onPillClick = () => {
    // Send the user back to My Day where the Mission card lives in
    // its Started state — gives a richer surface than the bar alone.
    const onMainApp =
      location.pathname === "/" || location.pathname === "/app";
    if (onMainApp) {
      window.dispatchEvent(
        new CustomEvent(SWITCH_TAB_EVENT, { detail: "myday" })
      );
    } else {
      navigate("/app?tab=myday");
    }
  };

  return (
    <>
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[150] flex flex-col items-center gap-2"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 8.5rem)" }}
        data-testid="active-mission-bar"
      >
        <div className="flex items-stretch gap-0 rounded-full bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-lg animate-fade-in overflow-hidden">
          <button
            type="button"
            onClick={onPillClick}
            aria-label={`Open active mission: ${mission.title}`}
            className="flex items-center gap-2 pl-4 pr-3 py-2 active:scale-[0.98] transition-transform"
            data-testid="active-mission-bar-open"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            <PillarIcon className="w-4 h-4" aria-hidden="true" />
            <span className="text-sm font-semibold truncate max-w-[160px]">
              {mission.title}
            </span>
            <ChevronRight className="w-4 h-4 opacity-70" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setCloseOpen(true)}
            aria-label="Mark mission complete"
            className="flex items-center gap-1.5 pl-3 pr-4 py-2 bg-white/15 hover:bg-white/25 active:scale-[0.98] transition-all border-l border-white/20"
            data-testid="active-mission-bar-complete"
          >
            <Check className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
            <span className="text-sm font-semibold">Complete</span>
          </button>
        </div>
      </div>

      {/* Reuses the existing MissionRitual Close beat so the +25 XP
          line, pillar pulse, and auto-dismiss are identical to the
          ritual fired from MissionCard. Pop it open in "close" mode
          and rely on its own onClose to hide. */}
      <MissionRitual
        mode={closeOpen ? "close" : null}
        mission={{
          source_type: mission.source_type,
          source_id: mission.source_id,
          title: mission.title,
          pillar: mission.pillar,
        }}
        onClose={() => setCloseOpen(false)}
      />
    </>
  );
}
